<script lang="ts">
    import { getContext, tick } from 'svelte'
    import { derived as sderived } from 'svelte/store'

    import type { HydriaDocument } from 'hydria/HydriaDocument'
    import { gettext as _, tgettext } from 'hydria/services/gettext'

    import Alignment from '../BaseFields/Alignment.svelte'
    import SchemeSelection from '../BaseFields/Scheme.svelte'
    import ImageSelection from '../Widgets/ImageSelection.svelte'
    import type { ISimpleHtmlSection } from '../models'
    import SchemedHtmlField from './Fields/SchemedHtmlField.svelte'
    import { SvelteSet } from 'svelte/reactivity'

    interface Props {
        sectionEdit: ISimpleHtmlSection
        sectionSaved: ISimpleHtmlSection
        disabled: boolean
        set?: (v: any) => void
    }

    let { sectionEdit, sectionSaved, disabled, set = (v) => {} }: Props = $props()

    function findStyle(source: string[], start: string) {
        const first = source.filter((s) => s.startsWith(`${start}-`))[0]
        return first != null ? first.slice(start.length + 1) : null
    }
    function setStyle(start: string, value: string | undefined) {
        const data = { styles: [...sectionEdit.styles.filter((s) => !s.startsWith(`${start}-`))] }
        if (value != null && value != '') data.styles.push(`${start}-${value}`)
        data.styles.sort()
        set(data)
    }

    const Document = getContext('Document') as HydriaDocument
    const images = sderived(Document.attachedImages, ($atts) => $atts)

    let alignClass: string | null = $derived(sectionEdit != null ? findStyle(sectionEdit.styles, 'align') : null)
    let savedAlignClass: string | null = $derived(sectionSaved != null ? findStyle(sectionSaved.styles, 'align') : null)

    let imgAlignClass: string | null = $derived(sectionEdit != null ? findStyle(sectionEdit.styles, 'bg-img') : null)
    let savedImgAlignClass: string | null = $derived(sectionSaved != null ? findStyle(sectionSaved.styles, 'bg-img') : null)

    // let enabledImageAligns: SvelteSet<string | null> = $state(new SvelteSet([null]))
    let enabledImageAligns: SvelteSet<string | null> = $derived(new SvelteSet(sectionEdit != null && sectionEdit.bg_image_id != null ? [null, 'start', 'end'] : [null]))

    $effect(() => {
        if (imgAlignClass != null && sectionEdit.bg_image_id == null) {
            tick().then(() => {
                setStyle('bg-img', undefined)
            })
        }
    })

    let schemeClass: string | null = $derived(sectionEdit != null ? findStyle(sectionEdit.styles, 'scheme') : null)
    let savedSchemeClass: string | null = $derived(sectionSaved != null ? findStyle(sectionSaved.styles, 'scheme') : null)

    function bgImgUrl(attId: string | undefined, altName: string | undefined = undefined) {
        if (attId == null) return ''
        const att = $images.filter((img) => img.id === attId)[0]
        if (att == null) return ''
        if (!altName) return att.url
        const alts = att.alt_image_sizes.filter((alt) => alt.sizename == altName)
        if (!alts.length) return ''
        return alts[0].url
    }
</script>

<div class="container mb-3">
    <div class="row">
        <div class="col">
            <SchemeSelection {disabled} value={schemeClass} saved={savedSchemeClass} save={(e) => setStyle('scheme', e)} />
        </div>
        <div class="col" style="min-width: 11rem;">
            <Alignment {disabled} value={alignClass} saved={savedAlignClass} save={(e) => setStyle('align', e)} />
        </div>
        <div class="col" style="min-width: 11rem;">
            <Alignment
                {disabled}
                label={tgettext('Alignement de l’image')}
                value={imgAlignClass}
                saved={savedImgAlignClass}
                save={(e) => setStyle('bg-img', e)}
                allowed={[null, 'start', 'end']}
                enabled={enabledImageAligns}
                aligns={[
                    { id: null, text: tgettext('Fond du texte'), icon: 'gridicons:align-image-center' },
                    { id: 'start', text: tgettext('Gauche'), icon: 'gridicons:align-image-left' },
                    { id: 'end', text: tgettext('Droit'), icon: 'gridicons:align-image-right' },
                ]}
            />
        </div>
        <div class="col">
            <ImageSelection
                {disabled}
                value={sectionEdit.bg_image_id}
                saved={sectionSaved ? sectionSaved.bg_image_id : undefined}
                allowEmpty={true}
                label={tgettext('Image de fond')}
                previewName={'vignette'}
                save={(e) => {
                    set({ bg_image_id: e })
                }}
            />
        </div>
    </div>
</div>

<SchemedHtmlField
    value={sectionEdit.content_html}
    saved={sectionSaved ? sectionSaved.content_html : undefined}
    {Document}
    styles={sectionEdit.styles}
    backgroundImageUrl={bgImgUrl(sectionEdit.bg_image_id)}
    label={tgettext('Texte')}
    {disabled}
    setValue={(e) => {
        set({ content_html: e })
    }}
/>
